import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ListPagination from "@/components/ListPagination";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import ClickableStatCard from "@/components/ClickableStatCard";
import { Button } from "@/components/ui/button";
import { Plus, BarChart3, Edit3, Trash2, Send, MailOpen, MousePointerClick, AlertTriangle, Download, Copy } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";

const StatusPill = ({ status, progress, rateLimitWaiting }) => {
  const map = {
    draft: { bg: "#FEF3C7", text: "#92400E", label: "Draft" },
    failed: { bg: "#FEE2E2", text: "#991B1B", label: "Send failed" },
    scheduled: { bg: "#DBEAFE", text: "#1E40AF", label: "Scheduled" },
    sending: {
      bg: rateLimitWaiting ? "#FFEDD5" : "#FEF3C7",
      text: rateLimitWaiting ? "#C2410C" : "#B45309",
      label: rateLimitWaiting
        ? (progress?.total ? `Waiting for limit ${progress.sent}/${progress.total}` : "Waiting for limit")
        : (progress?.total ? `Sending ${progress.sent}/${progress.total}` : "Sending"),
    },
    paused: { bg: "#FCE7F3", text: "#9D174D", label: progress?.total ? `Paused ${progress.sent}/${progress.total}` : "Paused" },
    staging: { bg: "#FEF3C7", text: "#B45309", label: "Preparing import" },
    sent: { bg: "#D1FAE5", text: "#065F46", label: "Sent" },
    testing: { bg: "#E0E7FF", text: "#3730A3", label: "Testing" },
  };
  const s = map[status] || { bg: "#F3F4F6", text: "#374151", label: status };
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: s.bg, color: s.text }}>
      {s.label}
    </span>
  );
};

export default function CampaignsPage() {
  const confirm = useConfirm();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [exportingOpens, setExportingOpens] = useState(false);

  const fetcher = useCallback((page, limit) => api.get("/campaigns", { params: { page, limit } }), []);

  const {
    items,
    total,
    pages,
    page,
    limit,
    setPage,
    setLimit,
    loading,
    reload,
  } = usePaginatedList(fetcher, []);

  const loadStats = useCallback(async (silent = false) => {
    if (!silent) setStatsLoading(true);
    try {
      const statsRes = await api.get("/dashboard/stats");
      setStats(statsRes.data);
    } catch {
      /* polling errors are non-fatal */
    } finally {
      if (!silent) setStatsLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(false); }, [loadStats]);

  useEffect(() => {
    const timer = setInterval(() => loadStats(true), 15000);
    return () => clearInterval(timer);
  }, [loadStats]);

  const hasActiveCampaigns = items.some(
    (c) => ["sending", "paused", "scheduled", "failed"].includes(c.status) || c.rate_limit_waiting,
  );

  useEffect(() => {
    if (!hasActiveCampaigns) return undefined;
    const timer = setInterval(() => reload(), 10000);
    return () => clearInterval(timer);
  }, [hasActiveCampaigns, reload]);

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete campaign?",
      description: "Delete this campaign? This action cannot be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/campaigns/${id}`);
    toast.success("Campaign deleted");
    reload();
    loadStats();
  };

  const copyCampaign = async (id) => {
    try {
      const res = await api.post(`/campaigns/${id}/copy`);
      toast.success("Campaign copied as draft");
      reload();
      loadStats();
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Copy failed");
      return null;
    }
  };

  const sent = items.filter((c) => c.status === "sent" || c.status === "testing");
  const firstAnalytics = sent[0]?.id;

  const exportAllOpens = async () => {
    setExportingOpens(true);
    try {
      const res = await api.get("/campaigns/export/opens/all", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "mailwave-all-campaign-opens.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported all campaign opens");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExportingOpens(false);
    }
  };

  const eng = (k) => (firstAnalytics ? `/campaigns/${firstAnalytics}/engagement/${k}` : "/campaigns");
  const summary = stats ? [
    { label: "Opens", value: (stats.total_opens ?? 0).toLocaleString(), Icon: MailOpen, to: eng("opens"), accent: "var(--brand-primary)" },
    { label: "Clicks", value: (stats.total_clicks ?? 0).toLocaleString(), Icon: MousePointerClick, to: eng("clicks"), accent: "var(--accent-red)" },
    { label: "Bounces", value: (stats.total_bounces ?? 0).toLocaleString(), Icon: AlertTriangle, to: eng("bounces"), accent: "#E0000F" },
    { label: "Open rate", value: stats.open_rate, suffix: "%", Icon: MailOpen, to: eng("opens"), accent: "var(--brand-primary)" },
  ] : [];

  return (
    <div data-testid="campaigns-page">
      <PageHeader
        title="Campaigns"
        subtitle="Compose, send, and analyze your email campaigns."
        action={
          <div className="flex flex-wrap gap-2">
            {!statsLoading && sent.length > 0 && (
              <Button
                variant="outline"
                onClick={exportAllOpens}
                disabled={exportingOpens}
                className="rounded-sm h-11 font-semibold"
                data-testid="export-all-opens-button"
              >
                <Download className="h-4 w-4 mr-2" />
                {exportingOpens ? "Exporting..." : "Export all opens (CSV)"}
              </Button>
            )}
            <Link to="/campaigns/new">
              <Button className="rounded-sm h-11 btn-brand font-semibold" data-testid="new-campaign-button">
                <Plus className="h-4 w-4 mr-2" /> New campaign
              </Button>
            </Link>
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-8">
        {!statsLoading && stats && sent.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {summary.map((it) => (
              <ClickableStatCard
                key={it.label}
                label={it.label}
                value={it.value}
                suffix={it.suffix}
                Icon={it.Icon}
                to={it.to}
                accent={it.accent}
                testId={`campaigns-stat-${it.label.toLowerCase().replace(/\s/g, "-")}`}
              />
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
        ) : total === 0 ? (
          <div className="text-center py-16">
            <Send className="h-16 w-16 text-neutral-300 mx-auto mb-6" />
            <h3 className="font-display font-bold text-2xl mb-2">Ready to launch your first campaign?</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-md mx-auto mb-6">
              Design a beautiful email, pick an audience, and send.
            </p>
            <Link to="/campaigns/new">
              <Button className="rounded-sm h-11 btn-brand font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Create campaign
              </Button>
            </Link>
          </div>
        ) : (
          <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                  <th className="text-left px-6 py-3 label-overline">Name</th>
                  <th className="text-left px-6 py-3 label-overline">Audience</th>
                  <th className="text-left px-6 py-3 label-overline">Status</th>
                  <th className="text-right px-6 py-3 label-overline">Recipients</th>
                  <th className="text-right px-6 py-3 label-overline">Opens</th>
                  <th className="text-right px-6 py-3 label-overline">Clicks</th>
                  <th className="text-right px-6 py-3 label-overline">Bounces</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors" data-testid={`campaign-row-${c.id}`}>
                    <td className="px-6 py-4">
                      <Link to={`/campaigns/${c.id}`} className="font-semibold text-neutral-900 dark:text-neutral-50 hover:text-[#002FA7] hover:underline underline-offset-2" data-testid={`campaign-name-${c.id}`}>
                        {c.name}
                      </Link>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{c.subject}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">{c.audience_name}</td>
                    <td className="px-6 py-4">
                      <StatusPill status={c.status} progress={c.send_progress} rateLimitWaiting={c.rate_limit_waiting} />
                      {c.send_error && (
                        <div
                          className={`text-xs mt-1 max-w-xs ${c.rate_limit_waiting ? "text-amber-700 dark:text-amber-400" : "text-red-600"}`}
                          title={c.send_error}
                        >
                          {c.send_error}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">{c.recipients ?? "—"}</td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      {(c.status === "sent" || c.status === "testing") ? (
                        <Link to={`/campaigns/${c.id}/engagement/opens`} className="underline underline-offset-2 hover:text-neutral-900 dark:hover:text-neutral-50">
                          {c.unique_opens ?? c.opens ?? 0}
                        </Link>
                      ) : (c.unique_opens ?? c.opens ?? "—")}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      {(c.status === "sent" || c.status === "testing") ? (
                        <Link to={`/campaigns/${c.id}/engagement/clicks`} className="underline underline-offset-2 hover:text-neutral-900 dark:hover:text-neutral-50">
                          {c.clicks ?? 0}
                        </Link>
                      ) : (c.clicks ?? "—")}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      {(c.status === "sent" || c.status === "testing") ? (
                        <Link to={`/campaigns/${c.id}/engagement/bounces`} className="underline underline-offset-2 hover:text-neutral-900 dark:hover:text-neutral-50">
                          {c.bounces ?? 0}
                        </Link>
                      ) : (c.bounces ?? "—")}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 justify-end">
                        <Link to={`/campaigns/${c.id}`} className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50" data-testid={`campaign-view-${c.id}`}>
                          <BarChart3 className="h-4 w-4" />
                        </Link>
                        {(c.status === "draft" || c.status === "failed" || c.status === "scheduled" || c.status === "paused") && (
                          <Link to={`/campaigns/${c.id}/edit`} className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50" data-testid={`campaign-edit-${c.id}`}>
                            <Edit3 className="h-4 w-4" />
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={() => copyCampaign(c.id)}
                          className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50"
                          title="Copy campaign"
                          data-testid={`campaign-copy-${c.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => remove(c.id)} className="text-neutral-400 hover:text-red-600" data-testid={`campaign-delete-${c.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ListPagination
              page={page}
              pages={pages}
              total={total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
